import { useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { ACT_AS, STOP_ACTING_AS } from "graphql/user";
import { reloadPage } from "./reloadPage";

/**
 * Switch the entity a super admin acts as (backend actAs / stopActingAs). The choice lives on the
 * user row and applies from the next request, so after the mutation we drop the Apollo cache and
 * reload: every query re-runs as the new principal and the nav/pages re-render for that role.
 */
export const ACT_AS_ROLES = {
  BANK: { admin: "BANK_ADMIN", user: "BANK_USER" },
  COMPANY: { admin: "COMPANY_ADMIN", user: "COMPANY_USER" },
  ORGANISATION: { admin: "ORGANISATION_ADMIN", user: "ORGANISATION_USER" },
};

export const formatRoleLabel = (role) =>
  role
    ? role
        .split("_")
        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
        .join(" ")
    : "";

const reloadAsNewPrincipal = async (client) => {
  try {
    await client.clearStore();
  } catch (_) {
    // The reload re-creates the cache anyway
  }
  reloadPage();
};

export const useActAs = () => {
  const client = useApolloClient();
  const [actAsMutation] = useMutation(ACT_AS);
  const [stopMutation] = useMutation(STOP_ACTING_AS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const actAs = async ({ type, id, role }) => {
    setBusy(true);
    setError(null);
    try {
      await actAsMutation({ variables: { input: { type, id, role } } });
      await reloadAsNewPrincipal(client);
    } catch (err) {
      console.error("[ActAs] actAs failed:", err);
      setError(err.message || "Could not switch");
      setBusy(false);
    }
  };

  const stopActingAs = async () => {
    setBusy(true);
    setError(null);
    try {
      await stopMutation();
      await reloadAsNewPrincipal(client);
    } catch (err) {
      console.error("[ActAs] stopActingAs failed:", err);
      setError(err.message || "Could not switch back");
      setBusy(false);
    }
  };

  return { actAs, stopActingAs, busy, error };
};
